import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Calendar, Lock } from 'lucide-react';
import { useSubsidiary, ALL_SUBSIDIARIES_SENTINEL } from '@/contexts/SubsidiaryContext';
import { usePeriod } from '@/contexts/PeriodContext';
import { cn } from '@/utils/cn';
import { pathMatches } from './navConfig';
import { statusInfo, type StatusTone } from '@/components/kit';
import ContextPicker from './ContextPicker';

/** Which pages read the shared subsidiary and period (the others ignore them, so the sidebar doesn't offer them
 *  there: a selector that changes nothing on the page is a trap). */
const PAGES_WITH_SUBSIDIARY = ['/dashboard', '/reconciliation', '/accounts', '/balance-sheet', '/admin/accounts', '/admin/periods',
  '/admin/assignments', '/delegations'];
// intercompany: the matrix, pairs and unassigned are all for the selected period (group-wide: no company picker)
const PAGES_WITH_PERIOD = ['/dashboard', '/reconciliation', '/accounts', '/balance-sheet', '/admin/periods', '/intercompany'];
/** Only the bank account list reads the period; a bank reconciliation, its import and the other bank pages are
 *  for their own month (offering a different one beside "Oct 2026" read as a contradiction). */
const PERIOD_EXACT = ['/bankrec'];

const under = (pathname: string, list: string[]) => list.some(p => pathMatches(pathname, p));
const isReconciliationDetail = (pathname: string) => /^\/reconciliation\/\d+$/.test(pathname);

export function contextFields(pathname: string) {
  return {
    subsidiary: under(pathname, PAGES_WITH_SUBSIDIARY) && !isReconciliationDetail(pathname),
    period: under(pathname, PAGES_WITH_PERIOD) || PERIOD_EXACT.includes(pathname),
  };
}

/** C3: a period's status as a small dot and a muted word ("● Open"), with a lock when it is locked or closed. The
 *  tone comes from the kit's status vocabulary (domain "period"); on the dark band the dots use the band's own,
 *  lighter status colours (--mp-nav-success / -warning), on the light popover the normal ones. */
const DOT_ON_BAND: Record<StatusTone, string> = {
  success: 'bg-mp-nav-success', warning: 'bg-mp-nav-warning', danger: 'bg-mp-nav-warning', info: 'bg-mp-nav-text', neutral: 'bg-mp-nav-text',
};
const DOT_ON_SURFACE: Record<StatusTone, string> = {
  success: 'bg-mp-success', warning: 'bg-mp-warning', danger: 'bg-mp-danger', info: 'bg-mp-info', neutral: 'bg-mp-text-muted',
};

export const PeriodStatus: React.FC<{ status?: string | null; onBand?: boolean }> = ({ status, onBand = false }) => {
  if (!status) return null;
  const { tone, label } = statusInfo(status, 'period');
  const locked = /^(LOCKED|CLOSED)$/i.test(status);
  return (
    <span className={cn('inline-flex items-center gap-1 font-normal', onBand ? 'text-mp-nav-text' : 'text-mp-text-muted')}
          data-period-status={status.toUpperCase()} data-tone={tone}>
      {locked ? <Lock className="h-3 w-3" aria-hidden /> : <span className={cn('h-1.5 w-1.5 rounded-full', (onBand ? DOT_ON_BAND : DOT_ON_SURFACE)[tone])} aria-hidden />}
      {label}
    </span>
  );
};

/** The company and period at the top of the sidebar (C3): ContextPicker rows on the band; collapsed to the icon rail,
 *  one icon each that opens the same list. */
const SidebarContext: React.FC<{ collapsed?: boolean; onExpand?: () => void }> = ({ collapsed }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { selectedSubsidiary, subsidiaries, setSelectedSubsidiary, isLoading: subsLoading } = useSubsidiary();
  const { selectedPeriod, periods, setSelectedPeriod, isLoading: periodsLoading } = usePeriod();
  const show = contextFields(pathname);
  if (!show.subsidiary && !show.period) return null;

  const subName = selectedSubsidiary && selectedSubsidiary.id !== 0 ? selectedSubsidiary.name : 'All subsidiaries';
  const periodName = selectedPeriod?.accounting_period?.period_name ?? 'No period';
  const periodStatus = selectedPeriod?.status ? statusInfo(selectedPeriod.status, 'period').label : '';

  const company = show.subsidiary && (
    <ContextPicker label="Company" icon={Building2} collapsed={collapsed} disabled={subsLoading} filterable
                   value={<span className="truncate">{subName}</span>} valueText={subName}
                   selectedId={selectedSubsidiary && selectedSubsidiary.id !== 0 ? String(selectedSubsidiary.id) : ''}
                   options={[{ id: '', label: 'All subsidiaries' }, ...subsidiaries.map(s => ({ id: String(s.id), label: s.name }))]}
                   onSelect={v => {
                     if (!v) { setSelectedSubsidiary(ALL_SUBSIDIARIES_SENTINEL); return; }
                     const s = subsidiaries.find(x => String(x.id) === v);
                     if (s) setSelectedSubsidiary(s);
                   }} />
  );
  const period = show.period && (
    <ContextPicker label="Period" icon={Calendar} collapsed={collapsed} disabled={periodsLoading}
                   value={<><span className="truncate">{periodName}</span>
                     {selectedPeriod?.status && <><span className="text-mp-nav-text" aria-hidden>·</span><PeriodStatus status={selectedPeriod.status} onBand /></>}</>}
                   valueText={periodStatus ? `${periodName}, ${periodStatus}` : periodName}
                   selectedId={selectedPeriod ? String(selectedPeriod.id) : ''}
                   options={periods.map(p => ({ id: String(p.id), label: p.accounting_period.period_name, extra: <PeriodStatus status={p.status} /> }))}
                   onSelect={v => {
                     const p = periods.find(x => String(x.id) === v) || null;
                     setSelectedPeriod(p);
                     // a reconciliation belongs to one period: changing it goes back to the list for the new one
                     if (isReconciliationDetail(pathname) && p) navigate('/reconciliation');
                   }} />
  );

  return (
    <div className={cn('border-b border-mp-nav-border', collapsed ? 'flex flex-col items-center gap-1 py-2' : 'space-y-1 px-2 py-1.5')}
         data-testid="sidebar-context">
      {company}
      {period}
    </div>
  );
};

export default SidebarContext;
